
import { useState, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Plus, Loader2, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react"
import { useAuth } from "@/lib/auth/auth-context"
import { toast } from "sonner"
import {
  fetchRecurringTransactions,
  deleteRecurringTransaction,
  toggleRecurringTransaction,
  formatFrequency
} from "@/lib/services/recurring-transactions"
import type { RecurringTransaction } from "@/lib/types"
import { AddRecurringTransaction } from "@/components/add-recurring-transaction"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface RecurringTransactionsProps {
  onBack: () => void
  onNavigate?: (view: string) => void
}

export function RecurringTransactions({ onBack, onNavigate }: RecurringTransactionsProps) {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<RecurringTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddTransaction, setShowAddTransaction] = useState(false)
  const [editTransaction, setEditTransaction] = useState<RecurringTransaction | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null)

  const loadTransactions = async () => {
    if (!user) return

    try {
      setLoading(true)
      const data = await fetchRecurringTransactions(user.id)
      setTransactions(data)
    } catch (error) {
      console.error('Error loading recurring transactions:', error)
      toast.error('Failed to load recurring transactions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTransactions()
  }, [user])

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await toggleRecurringTransaction(id, !currentStatus)
      toast.success(`Recurring transaction ${!currentStatus ? 'activated' : 'deactivated'}`)
      loadTransactions()
    } catch (error) {
      console.error('Error toggling recurring transaction:', error)
      toast.error('Failed to update recurring transaction')
    }
  }

  const handleEdit = (transaction: RecurringTransaction) => {
    setEditTransaction(transaction)
    setShowAddTransaction(true)
  }

  const handleDeleteClick = (id: string) => {
    setTransactionToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return

    try {
      await deleteRecurringTransaction(transactionToDelete)
      toast.success('Recurring transaction deleted successfully')
      loadTransactions()
    } catch (error) {
      console.error('Error deleting recurring transaction:', error)
      toast.error('Failed to delete recurring transaction')
    } finally {
      setDeleteDialogOpen(false)
      setTransactionToDelete(null)
    }
  }

  const handleCloseAdd = () => {
    setShowAddTransaction(false)
    setEditTransaction(null)
  }

  const handleSave = () => {
    loadTransactions()
  }

  const activeTransactions = transactions.filter(t => t.isActive)
  const inactiveTransactions = transactions.filter(t => !t.isActive)

  return (
    <div className="space-y-6 p-4 pb-24">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Recurring Transactions</h1>
            <p className="text-sm text-muted-foreground">Manage your recurring expenses & income</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddTransaction(true)}
          size="icon"
          className="rounded-full h-12 w-12 bg-primary hover:bg-primary/90"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      {loading ? (
        <Card className="p-8 shadow-sm border-border/50">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Card>
      ) : transactions.length === 0 ? (
        <Card className="p-8 shadow-md border-border/50">
          <div className="text-center space-y-4">
            <div className="mx-auto h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <div className="text-5xl">🔄</div>
            </div>
            <div>
              <h3 className="text-lg font-bold">No Recurring Transactions</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Set up recurring expenses and income to automate your tracking
              </p>
            </div>
            <Button onClick={() => setShowAddTransaction(true)} className="mt-4 shadow-sm bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {activeTransactions.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-muted-foreground">Active</h2>
              <div className="space-y-2">
                {activeTransactions.map((transaction) => (
                  <Card
                    key={transaction.id}
                    className="p-4 hover:shadow-md hover:scale-[1.01] transition-all duration-200 border-border/50"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-14 w-14 rounded-xl ${transaction.color} flex items-center justify-center text-2xl flex-shrink-0 shadow-sm`}>
                        {transaction.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold truncate">{transaction.name}</p>
                          <div className={`text-lg font-bold ${transaction.type === 'income' ? "text-income" : "text-expense"}`}>
                            {transaction.type === 'income' ? "+" : "-"}${Math.abs(transaction.amount).toFixed(2)}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {transaction.category} • {formatFrequency(
                            transaction.frequency,
                            transaction.dayOfWeek,
                            transaction.dayOfMonth
                          )}
                        </p>
                        {transaction.nextRunDate && (
                          <p className="text-xs text-muted-foreground mt-1.5">
                            Next: {new Date(transaction.nextRunDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(transaction)}
                            className="shadow-sm"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleToggleActive(transaction.id, transaction.isActive)}
                            className="shadow-sm"
                          >
                            <ToggleLeft className="h-3.5 w-3.5 mr-1.5" />
                            Pause
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(transaction.id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {inactiveTransactions.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-muted-foreground">Paused</h2>
              <div className="space-y-2">
                {inactiveTransactions.map((transaction) => (
                  <Card
                    key={transaction.id}
                    className="p-4 opacity-60 hover:opacity-100 hover:shadow-md hover:scale-[1.01] transition-all duration-200 border-dashed border-border/50"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-14 w-14 rounded-xl ${transaction.color} flex items-center justify-center text-2xl flex-shrink-0 shadow-sm`}>
                        {transaction.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold truncate">{transaction.name}</p>
                          <div className={`text-lg font-bold ${transaction.type === 'income' ? "text-income" : "text-expense"}`}>
                            {transaction.type === 'income' ? "+" : "-"}${Math.abs(transaction.amount).toFixed(2)}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {transaction.category} • {formatFrequency(
                            transaction.frequency,
                            transaction.dayOfWeek,
                            transaction.dayOfMonth
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(transaction)}
                            className="shadow-sm"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleToggleActive(transaction.id, transaction.isActive)}
                            className="bg-primary hover:bg-primary/90 shadow-sm"
                          >
                            <ToggleRight className="h-3.5 w-3.5 mr-1.5" />
                            Resume
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(transaction.id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showAddTransaction && (
        <AddRecurringTransaction
          onClose={handleCloseAdd}
          onSave={handleSave}
          editTransaction={editTransaction}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this recurring transaction? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
